import { useState, useCallback } from 'react';
import { apiClient } from '../api/client';

const GIS_SDK_URL = 'https://accounts.google.com/gsi/client';
const GAPI_URL = 'https://apis.google.com/js/api.js';
const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.readonly';

export type GoogleDriveStatus =
  | 'idle'
  | 'loading-sdk'
  | 'authorizing'
  | 'picking'
  | 'downloading'
  | 'done'
  | 'error';

export interface DrivePickResult {
  fileId: string;
  name: string;
  mimeType: string;
  url: string;
}

function getClientId(): string {
  const id = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (!id) throw new Error('VITE_GOOGLE_CLIENT_ID is not set');
  return id;
}

function getApiKey(): string {
  const key = import.meta.env.VITE_GOOGLE_API_KEY;
  if (!key) throw new Error('VITE_GOOGLE_API_KEY is not set');
  return key;
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

function requestDriveToken(clientId: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const client = window.google!.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: DRIVE_SCOPE,
      callback: (response) => {
        if (response.error) {
          reject(new Error(response.error_description || response.error));
        } else {
          resolve(response.access_token);
        }
      },
      error_callback: (err) => reject(new Error(err.message || 'OAuth failed')),
    });
    client.requestAccessToken();
  });
}

function openDrivePicker(accessToken: string, apiKey: string): Promise<DrivePickResult> {
  return new Promise((resolve, reject) => {
    window.gapi.load('picker', () => {
      // "Recent" tab — recently accessed files; picker's built-in search bar filters within it
      const recentView = new window.google!.picker.DocsView(
        window.google!.picker.ViewId.RECENTLY_PICKED,
      ).setSelectFolderEnabled(false);

      // "My Drive" tab — full folder navigation + search
      const folderView = new window.google!.picker.DocsView()
        .setLabel('My Drive')
        .setIncludeFolders(true)
        .setSelectFolderEnabled(false);

      // "Documents" tab — PDFs, Office files, Google Workspace docs/sheets/slides
      const documentMimeTypes = [
        'application/pdf',
        'application/vnd.google-apps.document',
        'application/vnd.google-apps.spreadsheet',
        'application/vnd.google-apps.presentation',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      ].join(',');
      const documentsView = new window.google!.picker.DocsView()
        .setLabel('Documents')
        .setMimeTypes(documentMimeTypes)
        .setIncludeFolders(false)
        .setSelectFolderEnabled(false);

      // "Images" tab — image files only
      const imagesView = new window.google!.picker.DocsView(
        window.google!.picker.ViewId.DOCS_IMAGES,
      ).setSelectFolderEnabled(false);

      // The picker appends .picker-dialog / .picker-dialog-bg nodes to <body>
      // and only hides them on close — leaked nodes keep floating over the
      // app at fixed viewport coordinates and can swallow hover/clicks on
      // whatever lies underneath. Dispose + sweep tears them down fully.
      const cleanup = () => {
        picker.setVisible(false);
        picker.dispose();
        document.querySelectorAll('.picker-dialog, .picker-dialog-bg').forEach((el) => el.remove());
      };
      const picker = new window.google!.picker.PickerBuilder()
        .addView(recentView)
        .addView(folderView)
        .addView(documentsView)
        .addView(imagesView)
        .setOAuthToken(accessToken)
        .setDeveloperKey(apiKey)
        .setCallback((data: unknown) => {
          const d = data as {
            action: string;
            docs: Array<{ id: string; name: string; mimeType: string; url: string }>;
          };
          if (d.action === window.google!.picker.Action.PICKED) {
            const doc = d.docs[0];
            cleanup();
            resolve({
              fileId: doc.id,
              name: doc.name,
              mimeType: doc.mimeType,
              url: doc.url,
            });
          } else if (d.action === window.google!.picker.Action.CANCEL) {
            cleanup();
            reject(new Error('Cancelled'));
          }
        })
        .build();
      picker.setVisible(true);
    });
  });
}

// Export mapping for Google Workspace files to their closest Office equivalents
const GOOGLE_APPS_EXPORT_MAP: Record<string, { mime: string; ext: string }> = {
  'application/vnd.google-apps.document': {
    mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ext: 'docx',
  },
  'application/vnd.google-apps.spreadsheet': {
    mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ext: 'xlsx',
  },
  'application/vnd.google-apps.presentation': {
    mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ext: 'pptx',
  },
};

async function uploadDriveFileToS3(
  fileId: string,
  token: string,
  fileName: string,
  mimeType: string,
  exportMime?: string,
): Promise<{ s3Key: string; fileName: string; mimeType: string; fileSizeBytes: number }> {
  const { data } = await apiClient.post<{
    s3Key: string;
    fileName: string;
    mimeType: string;
    fileSizeBytes: number;
  }>('/google-drive/upload-to-s3', {
    fileId,
    token,
    fileName,
    mimeType,
    ...(exportMime ? { exportMime } : {}),
  });
  return data;
}

export function useGoogleDrive() {
  const [status, setStatus] = useState<GoogleDriveStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const pickAndLink = useCallback(async (): Promise<DrivePickResult | null> => {
    setError(null);
    try {
      setStatus('loading-sdk');
      await Promise.all([loadScript(GIS_SDK_URL), loadScript(GAPI_URL)]);

      setStatus('authorizing');
      const token = await requestDriveToken(getClientId());

      setStatus('picking');
      const result = await openDrivePicker(token, getApiKey());
      setStatus('done');
      return result;
    } catch (err) {
      if ((err as Error).message === 'Cancelled') {
        setStatus('idle');
        return null;
      }
      setError((err as Error).message);
      setStatus('error');
      return null;
    }
  }, []);

  const pickAndDownload = useCallback(async (): Promise<{
    fileId: string;
    name: string;
    mimeType: string;
    // S3 upload (regular files)
    s3Key?: string;
    fileSizeBytes?: number;
    // Link fallback (unknown Workspace types that can't be exported)
    externalUrl?: string;
    externalFileId?: string;
  } | null> => {
    setError(null);
    try {
      setStatus('loading-sdk');
      await Promise.all([loadScript(GIS_SDK_URL), loadScript(GAPI_URL)]);

      setStatus('authorizing');
      const token = await requestDriveToken(getClientId());

      setStatus('picking');
      const picked = await openDrivePicker(token, getApiKey());

      const exportInfo = GOOGLE_APPS_EXPORT_MAP[picked.mimeType];
      const isUnknownWorkspaceType =
        picked.mimeType.startsWith('application/vnd.google-apps.') && !exportInfo;

      if (isUnknownWorkspaceType) {
        // Unknown Workspace type (Forms, Drawings, etc.) — store as external link
        setStatus('done');
        return {
          fileId: picked.fileId,
          name: picked.name,
          mimeType: picked.mimeType,
          externalUrl: picked.url,
          externalFileId: picked.fileId,
        };
      }

      setStatus('downloading');
      const downloadMime = exportInfo?.mime ?? picked.mimeType;
      const fileName = exportInfo ? `${picked.name}.${exportInfo.ext}` : picked.name;

      console.log(
        `[drive] uploading to S3 server-side fileId=${picked.fileId} name=${fileName} mime=${downloadMime}`,
      );
      const result = await uploadDriveFileToS3(
        picked.fileId,
        token,
        fileName,
        downloadMime,
        exportInfo?.mime,
      );
      console.log(`[drive] S3 upload complete s3Key=${result.s3Key} size=${result.fileSizeBytes}`);

      setStatus('done');
      return {
        fileId: picked.fileId,
        name: result.fileName,
        s3Key: result.s3Key,
        mimeType: result.mimeType,
        fileSizeBytes: result.fileSizeBytes,
      };
    } catch (err) {
      if ((err as Error).message === 'Cancelled') {
        setStatus('idle');
        return null;
      }
      setError((err as Error).message);
      setStatus('error');
      return null;
    }
  }, []);

  return { pickAndLink, pickAndDownload, status, error };
}
